import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Same-origin API base, keyed on PAGE PROTOCOL (the pattern set in App.tsx — the
// canonical rule every page, incl. future domain pages, inherits). NOT NODE_ENV: the
// CRA dev server is `development` even behind an HTTPS front (Tailscale Serve), so
// keying on env appended :5000 under HTTPS and pointed at nothing.
//   • HTTPS page → same-origin :443, no port (herexy nginx / Tailscale Serve path-mount).
//   • HTTP page  → Flask :5000 direct — the countersigned LAN/Electron path, unchanged.
const API_BASE = window.location.protocol === 'https:'
  ? `https://${window.location.host}/api`
  : `${window.location.protocol}//${window.location.hostname}:5000/api`;

interface AuditEntry {
  timestamp: string;
  event: string;
  details: Record<string, any>;
  hash?: string;
}

interface AgentFinding {
  status: 'ALL_CLEAR' | 'WARNING' | 'ALERT';
  alerts: string[];
  [key: string]: any;
}

interface PulseFindings {
  timestamp: string;
  overall_status: string;
  WARDEN: AgentFinding;
  CIPHER: AgentFinding;
  SCOUT: AgentFinding;
  AEGIS: AgentFinding;
  TRACE: AgentFinding;
  PROXY: AgentFinding;
}

interface ChatMessage {
  // RECITE-WIRING-DELTA — 'system' is the INTERFACE voice: hardcoded errors/
  // statuses are never attributed to Santi; only his model-produced replies are.
  role: 'santi' | 'user' | 'system';
  text: string;
  severity?: 'error' | 'status';
}

const AGENT_ROLES: Record<string, string> = {
  WARDEN: 'Auth + output observer',
  CIPHER: 'Encryption + restarts',
  SCOUT: 'Rate limits + IP patterns',
  AEGIS: 'Overrides + probe detection',
  TRACE: 'Hash chain + timestamps',
  PROXY: 'Drive + external data',
};

const LOG_CATEGORIES: Record<string, string[]> = {
  auth: ['AUTH_SUCCESS', 'AUTH_FAILURE', 'AUTH_REJECTED'],
  output: ['OUTPUT_BLOCKED', 'OUTPUT_WARNED'],
  system: ['SYSTEM_START', 'SENTINEL_PULSE', 'SENTINEL_PROBE'],
  drive: ['DRIVE_LIST', 'DRIVE_READ', 'DRIVE_UPLOAD', 'PDF_EXPORT'],
  rate: ['IP_RATE_LIMITED', 'RATE_LIMITED', 'ENDPOINT_RATE_LIMITED'],
};

const HIGH_EVENTS = new Set(['OUTPUT_BLOCKED', 'IP_RATE_LIMITED', 'AUTH_REJECTED', 'AGENT_TIMEOUT', 'SENTINEL_PROBE']);
const MEDIUM_EVENTS = new Set(['ENDPOINT_RATE_LIMITED', 'OUTPUT_WARNED', 'RATE_LIMITED', 'AUTH_FAILURE']);

function getRisk(event: string): 'high' | 'medium' | 'low' {
  if (HIGH_EVENTS.has(event)) return 'high';
  if (MEDIUM_EVENTS.has(event)) return 'medium';
  return 'low';
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
}

const C = {
  bg:         '#0d0f14',
  card:       '#13161e',
  cardBorder: '#1e2130',
  header:     '#1a1d2a',
  text:       '#e2e8f0',
  textMuted:  '#6b7280',
  textDim:    '#94a3b8',
  accent:     '#6366f1',
  green:      '#22c55e',
  red:        '#ef4444',
  amber:      '#f59e0b',
  inputBg:    '#1a1d2a',
  inputBorder:'#2a2d3e',
  userBubble: '#4f46e5',
  agentBubble:'#1e2130',
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  const color = status === 'ALERT' ? C.red : status === 'WARNING' ? C.amber : C.green;
  return (
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: color, display: 'inline-block', flexShrink: 0,
      boxShadow: `0 0 6px ${color}`,
    }} />
  );
};

const RiskPill: React.FC<{ risk: string }> = ({ risk }) => {
  const styles: Record<string, React.CSSProperties> = {
    high:   { background: 'rgba(239,68,68,0.15)',  color: '#f87171' },
    medium: { background: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
    low:    { background: 'rgba(34,197,94,0.15)',  color: '#4ade80' },
  };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 7px',
      borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0,
      marginTop: 1, letterSpacing: '0.03em',
      ...styles[risk] ?? styles.low,
    }}>
      {risk.toUpperCase()}
    </span>
  );
};

const SecurityPage: React.FC<{ authToken: string | null; onBack?: () => void }> = ({ authToken, onBack }) => {
  const [flags, setFlags] = useState<AuditEntry[]>([]);
  const [allLog, setAllLog] = useState<AuditEntry[]>([]);
  const [pulse, setPulse] = useState<PulseFindings | null>(null);
  const [summary, setSummary] = useState<Record<string, any>>({});
  const [logFilter, setLogFilter] = useState<string>('all');
  const [chainStatus, setChainStatus] = useState<string>('Verified');
  const [chainCount, setChainCount] = useState<number>(0);
  const [lastPulse, setLastPulse] = useState<string>('—');
  // RECITE-WIRING-DELTA — the opener is INTERFACE state, not Santi speaking:
  // a hardcoded string is never attributed to an agent.
  const [chat, setChat] = useState<ChatMessage[]>([
    {
      role: 'system',
      severity: 'status',
      text: 'SENTINEL monitoring active — audit log observed, pulse checks every 30 minutes. Ask Santi about recent activity, specific events, or the security stack.',
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [santiModel, setSantiModel] = useState('local');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  useEffect(() => {
    fetchSecurity();
    fetchPulse();
  }, []);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  async function fetchSecurity() {
    try {
      const res = await axios.get(`${API_BASE}/security?level=low`, { headers });
      setFlags(res.data.flags || []);
      setAllLog(res.data.flags || []);
      setChainCount(res.data.total_events || 0);
    } catch (e) {
      console.error('Security fetch failed', e);
    }
  }

  async function fetchPulse() {
    try {
      const res = await axios.get(`${API_BASE}/security/pulse`, { headers });
      if (res.data.success) {
        setPulse(res.data.findings);
        setSummary(res.data.summary || {});
        setLastPulse(formatTime(res.data.findings?.timestamp || ''));
      }
    } catch (e) {
      console.error('Pulse fetch failed', e);
    }
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
    e.target.value = '';
  }

  async function sendChatMessage() {
    const text = chatInput.trim();
    if (!text || chatLoading) return;

    setChatInput('');
    setChat(prev => [...prev, { role: 'user', text }]);
    setChatLoading(true);

    try {
      let response: string | undefined;

      if (santiModel === 'local') {
        // Route to local security chat endpoint
        const formData = new FormData();
        formData.append('message', text);
        formData.append('context', JSON.stringify({
          pulse,
          summary,
          recentFlags: flags.slice(0, 20),
        }));
        if (uploadedFile) {
          formData.append('file', uploadedFile);
        }

        const res = await axios.post(`${API_BASE}/security/chat`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });
        response = res.data.reply || res.data.message || undefined;
      } else {
        // Route to external model endpoint (e.g. OpenAI-compatible)
        const res = await axios.post(`${API_BASE}/security/chat/external`, {
          message: text,
          model: santiModel,
          context: {
            pulse,
            summary,
            recentFlags: flags.slice(0, 20),
          },
        }, { headers });
        response = res.data.reply || res.data.message || undefined;
      }

      setUploadedFile(null);
      // RECITE-WIRING-DELTA — only a genuine model reply renders as Santi;
      // an empty reply is a SYSTEM state the interface reports.
      if (response && response.trim()) {
        setChat(prev => [...prev, { role: 'santi', text: response as string }]);
      } else {
        setChat(prev => [...prev, { role: 'system', severity: 'error', text: 'No reply returned from the Santi endpoint.' }]);
      }
    } catch (e: any) {
      console.error('Santi chat error', e);
      // RECITE-WIRING-DELTA — errors are SYSTEM notices, never Santi's line.
      const errorMsg = e?.response?.data?.error
        || e?.message
        || 'Request to the Santi endpoint failed. Check the console for details.';
      setChat(prev => [...prev, { role: 'system', severity: 'error', text: errorMsg }]);
    } finally {
      setChatLoading(false);
    }
  }

  function handleChatKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  }

  const filteredLog = logFilter === 'all'
    ? allLog
    : allLog.filter(entry => LOG_CATEGORIES[logFilter]?.includes(entry.event));

  const overallStatus = pulse?.overall_status || 'ALL_CLEAR';

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto', fontFamily: 'sans-serif', background: C.bg, minHeight: '100vh', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: C.textDim }}>
            ←
          </button>
        )}
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Security Console</h1>
        <span style={{
          marginLeft: 'auto', fontSize: 12, padding: '3px 10px', borderRadius: 12, fontWeight: 600,
          background: overallStatus === 'ALERT' ? 'rgba(239,68,68,0.15)' : overallStatus === 'WARNING' ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
          color: overallStatus === 'ALERT' ? '#f87171' : overallStatus === 'WARNING' ? '#fbbf24' : '#4ade80',
          border: `1px solid ${overallStatus === 'ALERT' ? 'rgba(239,68,68,0.3)' : overallStatus === 'WARNING' ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`,
        }}>
          {overallStatus}
        </span>
      </div>

      {/* Agent Grid */}
      {pulse && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          {Object.keys(AGENT_ROLES).map(agent => {
            const finding = pulse[agent as keyof PulseFindings] as AgentFinding;
            return (
              <div key={agent} style={{
                background: C.card, border: `1px solid ${C.cardBorder}`,
                borderRadius: 10, padding: '12px 14px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <StatusDot status={finding?.status || 'ALL_CLEAR'} />
                  <span style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{agent}</span>
                </div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{AGENT_ROLES[agent]}</div>
                {finding?.alerts?.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#f87171' }}>
                    {finding.alerts[0]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Chain Events', value: chainCount },
          { label: 'Chain Status', value: chainStatus },
          { label: 'Last Pulse', value: lastPulse },
          { label: 'Flagged Events', value: flags.filter(f => getRisk(f.event) === 'high').length },
        ].map(stat => (
          <div key={stat.label} style={{
            background: C.card, border: `1px solid ${C.cardBorder}`,
            borderRadius: 10, padding: '12px 18px', minWidth: 120,
          }}>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{stat.label}</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.accent }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Main Content: Log + Chat */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Audit Log */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.cardBorder}`, display: 'flex', alignItems: 'center', gap: 10, background: C.header }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>Audit Log</span>
            <select
              value={logFilter}
              onChange={e => setLogFilter(e.target.value)}
              style={{
                marginLeft: 'auto', fontSize: 12, padding: '3px 8px', borderRadius: 6,
                border: `1px solid ${C.inputBorder}`, background: C.inputBg, color: C.text, outline: 'none',
              }}
            >
              <option value="all">All</option>
              {Object.keys(LOG_CATEGORIES).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {filteredLog.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>No events found.</div>
            ) : (
              filteredLog.map((entry, i) => {
                const risk = getRisk(entry.event);
                return (
                  <div key={i} style={{
                    padding: '10px 16px', borderBottom: `1px solid ${C.cardBorder}`,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{entry.event}</span>
                        <RiskPill risk={risk} />
                      </div>
                      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                        {formatTimestamp(entry.timestamp)}
                        {entry.details?.ip && ` · ${entry.details.ip}`}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Santi Chat */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.cardBorder}`, display: 'flex', alignItems: 'center', gap: 10, background: C.header }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>Santi</span>
            <select
              value={santiModel}
              onChange={e => setSantiModel(e.target.value)}
              style={{
                marginLeft: 'auto', fontSize: 12, padding: '3px 8px', borderRadius: 6,
                border: `1px solid ${C.inputBorder}`, background: C.inputBg, color: C.text, outline: 'none',
              }}
            >
              <option value="local">Local</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
            </select>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', maxHeight: 340 }}>
            {chat.map((msg, i) => (
              // RECITE-WIRING-DELTA — 'system' renders as an INTERFACE notice
              // row (left accent strip: red for errors, muted for status),
              // visually distinct from both bubbles; never the agent idiom.
              msg.role === 'system' ? (
                <div key={i} style={{ marginBottom: 12, display: 'flex' }}>
                  <div style={{
                    width: '100%', display: 'flex', borderRadius: 8, overflow: 'hidden',
                    background: C.bg, border: `1px solid ${C.cardBorder}`,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                  }}>
                    <div style={{
                      width: 3, alignSelf: 'stretch', flexShrink: 0,
                      background: msg.severity === 'status' ? C.textMuted : C.red,
                      opacity: 0.7,
                    }} />
                    <div style={{ padding: '8px 12px', flex: 1 }}>
                      <div style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
                        color: msg.severity === 'status' ? C.textMuted : C.red, marginBottom: 3,
                      }}>
                        {msg.severity === 'status' ? '▸ SYSTEM' : '▸ SYSTEM · ERROR'}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5, color: C.textDim }}>{msg.text}</div>
                    </div>
                  </div>
                </div>
              ) : (
              <div key={i} style={{
                marginBottom: 12,
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '80%', fontSize: 13, lineHeight: 1.5,
                  padding: '8px 12px', borderRadius: 10,
                  background: msg.role === 'user' ? C.userBubble : C.agentBubble,
                  color: C.text,
                  border: msg.role === 'user' ? 'none' : `1px solid ${C.cardBorder}`,
                }}>
                  {msg.text}
                </div>
              </div>
              )
            ))}
            {chatLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                <div style={{
                  padding: '8px 12px', borderRadius: 10,
                  background: C.agentBubble, border: `1px solid ${C.cardBorder}`,
                  fontSize: 13, color: C.textMuted,
                }}>
                  Santi is thinking…
                </div>
              </div>
            )}
            <div ref={chatBottomRef} />
          </div>

          {/* File preview */}
          {uploadedFile && (
            <div style={{ padding: '6px 16px', background: 'rgba(34,197,94,0.1)', fontSize: 12, color: '#4ade80', display: 'flex', alignItems: 'center', gap: 8, borderTop: `1px solid ${C.cardBorder}` }}>
              <span>📎 {uploadedFile.name}</span>
              <button
                onClick={() => setUploadedFile(null)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: 13 }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Input Row */}
          <div style={{ padding: '10px 12px', borderTop: `1px solid ${C.cardBorder}`, display: 'flex', gap: 8, alignItems: 'center', background: C.header }}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: 'none', border: `1px solid ${C.inputBorder}`, borderRadius: 8,
                cursor: 'pointer', padding: '6px 10px', fontSize: 14, color: C.textMuted,
              }}
              title="Attach file"
            >
              📎
            </button>
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              placeholder="Ask Santi about security events…"
              disabled={chatLoading}
              style={{
                flex: 1, fontSize: 13, padding: '7px 12px',
                border: `1px solid ${C.inputBorder}`, borderRadius: 8, outline: 'none',
                background: C.inputBg, color: C.text,
              }}
            />
            <button
              onClick={sendChatMessage}
              disabled={chatLoading || !chatInput.trim()}
              style={{
                background: C.accent, color: '#fff', border: 'none',
                borderRadius: 8, padding: '7px 14px', fontSize: 13,
                cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer',
                opacity: chatLoading || !chatInput.trim() ? 0.5 : 1,
              }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;